import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import store, { snackbarActions } from '@apply/adminStore';
import { setAppError } from './common/SharedEvents';
import { IconButton, useMediaQuery, ClickAwayListener } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import { useSelector } from 'react-redux';
import { BfiApi, LoginApi } from '@apply/api';
import { applicationInsights } from './ApplicationInsights';
import { Header, Sidebar, ErrorModal } from './common/SharedComponents';
import { BfiSnackbar, LoadingModal, ExpiredTokenModal } from '@apply/ui';
import { WaitOverlay } from '@apply/components';
import Paper from '@material-ui/core/Paper';
import { withStyles, useTheme } from '@material-ui/core/styles/index';
import withAITracking from './ApplicationInsights';
import { ErrorBoundary } from './ErrorBoundary';
import 'devextreme/dist/css/dx.light.css';
import { FeatureType } from '@apply/constants';
import { handleAdminError, handleAdminSuccess } from './common/SharedFunctions';
import { isFeatureEnabled } from '@apply/store/src/user/types.ts';
import { AppContext } from './AppContext';
import { one } from 'devextreme/events';

const styles = (theme) => ({
    root: {
        display: 'flex',
        height: '100vh',
    },

    contentShownNav: {
        flexGrow: 1,
        height: '100%',
        maxWidth: 'calc(100vw - 200px)',
        [theme.breakpoints.down('sx')]: {
            content: '',
        },
    },

    contentHiddenNav: {
        flexGrow: 1,
        height: '100%',
        maxWidth: 'calc(100vw)',
        [theme.breakpoints.down('sx')]: {
            content: '',
        },
    },

    mobileContent: {
        padding: '85px 0px 20px 0px',
    },
});

function App(props) {
    const { snackbar } = store.getState();
    const api = new BfiApi.BfiApi(applicationInsights);
    const loginApi = new LoginApi.LoginApi(applicationInsights);

    const theme = useTheme();
    const isLarger = useMediaQuery(theme.breakpoints.up(1024));
    const location = useLocation();
    const authenticated = loginApi.isAuthenticated();
    const app = useSelector((state) => state.app);
    const bfiUser = useSelector((state) => state.bfiUser);
    const user = useSelector((state) => state.user);

    const { classes } = props;

    const [state, changeState] = useState({
        authenticated,
        admin: false,
        isSuper: false,
        modalIsOpen: false,
        isBroker: false,
        areTermsAccepted: false,
        userId: '',
        refreshStarted: false,
        loading: true,
    });

    function isJsonFormatCorrect(str) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    const [toggleStatus, setToggleStatus] = useState(false);

    const closeNav = () => setToggleStatus(false);

    const refreshToken = () => {
        setState({ refreshStarted: true });
        const ffinUserToken = window.localStorage.getItem('ffinuser');
        let token;
        if (ffinUserToken != null && isJsonFormatCorrect(ffinUserToken)) {
            const userTokenLS = JSON.parse(ffinUserToken);
            if (isJsonFormatCorrect(userTokenLS.value)) {
                token = JSON.parse(userTokenLS.value);
            } else {
                token = null;
            }
        } else {
            token = null;
        }

        const bfiRefreshTokenLS = window.localStorage.getItem('bfiRefreshToken');
        let refresh;
        if (bfiRefreshTokenLS != null && isJsonFormatCorrect(bfiRefreshTokenLS)) {
            const refreshTokenLS = JSON.parse(bfiRefreshTokenLS);
            if (isJsonFormatCorrect(refreshTokenLS.value)) {
                refresh = JSON.parse(refreshTokenLS.value);
            } else {
                refresh = null;
            }
        } else {
            refresh = null;
        }

        if (token && refresh) {
            api.refreshAdminToken(token, refresh)
                .then((success) => {
                    if (success) {
                        const { bfiUser } = store.getState();
                        setState({
                            authenticated: true,
                            loading: false,
                            admin: bfiUser.admin,
                            isBroker: bfiUser.isBroker,
                            areTermsAccepted: bfiUser?.areTermsAccepted,
                            roles: bfiUser.roles,
                        });
                    } else {
                        setState({ authenticated: false, loading: false });
                    }
                })
                .catch(() => {
                    // Refresh failed, send to the log in page
                    setState({ authenticated: false, loading: false });
                });
        } else {
            // No refresh token, send to the log in page
            setState({ authenticated: false, loading: false });
        }
    };

    useEffect(() => {
        closeNav();
    }, [location]);

    useEffect(() => {
        if ((!state.refreshStarted && !authenticated) || (bfiUser.isBroker && !bfiUser?.broker?.activated)) {
            refreshToken();
        } else {
            setState({ loading: false });
        }
        Modal.setAppElement(document.getElementById('root'));
    }, []);

    const setState = (changing) => {
        changeState({ ...state, ...changing });
    };

    const updateAuthentication = (_authenticated, callback) => {
        if (callback) {
            setState({ authenticated: _authenticated }, callback);
        } else {
            setState({ authenticated: _authenticated });
        }
    };

    const setAdmin = (admin) => {
        setState({ admin });
    };

    const setSuper = (isSuper) => {
        setState({ isSuper });
    };

    const setBroker = (isBroker) => {
        setState({ isBroker });
    };
    const setUserId = (userId) => {
        setState({ userId });
    };

    const performLogout = async () => {
        await loginApi.logout();
        updateAuthentication(false);
    };

    const handleLogout = async () => {
        await performLogout();
        window.location = '/';
    };

    const handleErrorModal = () => {
        setAppError(!app.error.status, '');
    };

    const handleNavToggle = () => {
        setToggleStatus(!toggleStatus);
    };

    const refreshBroker = () => {
        try {
            refreshToken();
            handleAdminSuccess('Your status was refreshed successfully');
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred please try again');
        }
    };

    const isNavOpen = isLarger ? true : toggleStatus;

    const oneHour = 3_600_000;
    const fiveMinutes = 300_000;

    return (
        <AppContext.Provider value={{ setAdmin, setSuper, setBroker, setUserId, updateAuthentication, refreshBroker, bfiUser }}>
            <ErrorBoundary>
                {app.loading.status === true && (
                    <LoadingModal
                        open={app.loading.status}
                        message={app.loading.message}
                    />
                )}
                <div>
                    {app.error.status && (
                        <ErrorModal
                            open={app.error.status}
                            handleClose={handleErrorModal}
                            message={app.error.message}
                        />
                    )}
                    {user?.admin ? (
                        <Paper className={classes.root}>
                            {state.authenticated && (
                                <>
                                    <Header notifyError={handleAdminError}>
                                        {!isLarger && (
                                            <ClickAwayListener onClickAway={closeNav}>
                                                <IconButton
                                                    className='nav_toggle'
                                                    color='inherit'
                                                    aria-label='menu'
                                                    onClick={handleNavToggle}
                                                >
                                                    {isNavOpen ? <CloseIcon /> : <MenuIcon />}
                                                </IconButton>
                                            </ClickAwayListener>
                                        )}
                                    </Header>
                                    <Sidebar
                                        open={isNavOpen}
                                        location={location}
                                    />
                                    <ExpiredTokenModal
                                        onLogout={handleLogout}
                                        timeout={oneHour}
                                        promptBeforeIdle={fiveMinutes}
                                    />
                                </>
                            )}

                            <Paper
                                className={isNavOpen ? classes.contentShownNav : classes.contentHiddenNav}
                                style={{
                                    display: isNavOpen && !isLarger ? 'none' : 'unset',
                                }}
                                square={true}
                                elevation={0}
                            >
                                {!state.loading && <Outlet />}
                            </Paper>
                        </Paper>
                    ) : (
                        <div>
                            {state.authenticated && (
                                <ExpiredTokenModal
                                    onLogout={handleLogout}
                                    timeout={oneHour}
                                    promptBeforeIdle={fiveMinutes}
                                />
                            )}
                            {!state.loading && <Outlet />}
                        </div>
                    )}
                </div>
                <BfiSnackbar
                    open={snackbar.isOpen}
                    message={snackbar.message}
                    variant={snackbar.variant}
                    handleClose={() => store.dispatch(snackbarActions.updateSnackbar({ isOpen: false }))}
                />
                <WaitOverlay logoSrc='/images/broker/logo.webp.html' />
            </ErrorBoundary>
        </AppContext.Provider>
    );
}

export default withAITracking(withStyles(styles)(App));
